/**
 * ステッカーランダム配置
 * .event-right 内の .text-sticker / .image-sticker を見切れないランダム位置に配置する
 *
 * transform: scale() + transform-origin: top left の場合、
 * left/top で指定した位置が視覚的な左上端になるため、
 * getBoundingClientRect() のサイズ（scale適用後）をそのまま使える。
 */

import { getStickerRightLimit, getVisualRight, fitStickerScale } from './sticker-position.js';

/** 2つの矩形の重なり面積を返す */
function overlapArea(r1, r2) {
  const x = Math.max(0, Math.min(r1.right, r2.right) - Math.max(r1.left, r2.left));
  const y = Math.max(0, Math.min(r1.bottom, r2.bottom) - Math.max(r1.top, r2.top));
  return x * y;
}

/** 偶数indexは左半分、奇数indexは右半分の範囲を返す */
function sideRange(maxX, index) {
  const half = maxX / 2;
  return index % 2 === 0 ? [0, half] : [half, maxX];
}

function placeStickers() {
  const rightLimit = getStickerRightLimit();

  document.querySelectorAll('.event-right').forEach((container, index) => {
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const containerRect = container.getBoundingClientRect();

    // text-sticker を先に配置
    const textSticker = container.querySelector('.text-sticker');
    if (textSticker) {
      // ビューポート左右に --padding-x の余白を確保した幅に収まるよう縮小する。
      // 縮小後に width を測るので、以降の配置計算は収まったサイズが前提になる。
      const paddingX = window.innerWidth - rightLimit;
      fitStickerScale(textSticker, Math.max(0, rightLimit - paddingX));
      const rect = textSticker.getBoundingClientRect();
      // layout-zigzag/flow は .text-sticker-blocks が右にオフセットされて
      // 親の box right より右に伸びる。視覚的な右端を取得する。
      const textVisualExtent = getVisualRight(textSticker) - rect.left;

      // ステッカーエリア高さ用: 描画幅が 220px 以上なら data-wide を付与
      if (rect.width >= 220) {
        textSticker.setAttribute('data-wide', '');
      } else {
        textSticker.removeAttribute('data-wide');
      }

      const maxX = Math.max(0, cw - rect.width);
      const maxY = Math.max(0, ch - rect.height);
      const [minL, maxL] = sideRange(maxX, index);
      let left = minL + Math.random() * (maxL - minL);

      // ビューポート右端から --padding-x 以上のマージンを確保
      const rightOverflow = containerRect.left + left + textVisualExtent - rightLimit;
      if (rightOverflow > 0) left -= rightOverflow;
      // ビューポート左端からも --padding-x 以上のマージンを確保（最後に適用して左余白を優先）
      const leftUnderflow = paddingX - (containerRect.left + left);
      if (leftUnderflow > 0) left += leftUnderflow;

      textSticker.style.left = `${left}px`;
      textSticker.style.top = `${Math.random() * maxY}px`;
      textSticker.classList.add('is-placed');
    }

    // image-sticker を重なりを考慮して配置
    const imageSticker = container.querySelector('.image-sticker');
    if (!imageSticker) return;

    const imgRect = imageSticker.getBoundingClientRect();
    const iw = imgRect.width;
    const ih = imgRect.height;
    const imgArea = iw * ih;
    const maxX = Math.max(0, cw - iw);
    const maxY = Math.max(0, ch - ih);
    const [imgMinL, imgMaxL] = sideRange(maxX, index);
    const textRect = textSticker ? textSticker.getBoundingClientRect() : null;
    const maxOverlap = 0.2; // 重なり面積がimage-stickerの20%以下ならOK

    let bestLeft = 0;
    let bestTop = 0;
    let bestOverlap = Infinity;
    let trials = 0;

    for (let i = 0; i < 100; i++) {
      trials = i + 1;
      const left = imgMinL + Math.random() * (imgMaxL - imgMinL);
      const top = Math.random() * maxY;

      if (!textRect || imgArea === 0) {
        bestLeft = left;
        bestTop = top;
        break;
      }

      // 候補位置での矩形（コンテナ基準の絶対座標）
      const candidateRect = {
        left: containerRect.left + left,
        top: containerRect.top + top,
        right: containerRect.left + left + iw,
        bottom: containerRect.top + top + ih,
      };

      const overlap = overlapArea(candidateRect, textRect) / imgArea;

      if (overlap < bestOverlap) {
        bestLeft = left;
        bestTop = top;
        bestOverlap = overlap;
      }

      if (overlap <= maxOverlap) break;
    }

    // ビューポート右端から --padding-x 以上のマージンを確保（子孫はみ出しも含めて測定）
    const imgVisualExtent = getVisualRight(imageSticker) - imgRect.left;
    const imgRightOverflow = containerRect.left + bestLeft + imgVisualExtent - rightLimit;
    if (imgRightOverflow > 0) bestLeft -= imgRightOverflow;

    imageSticker.style.left = `${bestLeft}px`;
    imageSticker.style.top = `${bestTop}px`;
    imageSticker.setAttribute('data-trials', trials);
    imageSticker.setAttribute('data-overlap', Math.round(bestOverlap * 100));
    const svg = imageSticker.querySelector('svg');
    if (svg) svg.style.transform = `rotate(${Math.random() * 60 - 30}deg)`;
    imageSticker.classList.add('is-placed');
  });
}

// フォント読み込み後に配置（サイズが確定してから）
if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(placeStickers);
} else {
  window.addEventListener('load', placeStickers);
}

// AJAX でイベントが追加されたとき再配置
document.addEventListener('danc:events-loaded', placeStickers);

// リサイズ時に再配置（iOS のアドレスバー表示/非表示による高さ変化では発火させない）
let resizeTimer;
let lastWidth = window.innerWidth;
window.addEventListener('resize', () => {
  if (window.innerWidth === lastWidth) return;
  lastWidth = window.innerWidth;
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(placeStickers, 200);
});
